import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WORLD_SUBJECTS, type SubjectKey } from "../content/world";
import { claimWorldChest, loadWorldState, readWorldState, type WorldState } from "../services/world/worldProgress";
import { useAppStore } from "../store/useAppStore";
import { preloadWorldBackground, worldBackgroundUrl } from "../lib/learningImages";
import { useLearningImage } from "../lib/useLearningImage";

const chapterCheckpoints = [10, 20] as const;
const chestRewards: Record<number, number> = { 5: 5, 10: 8, 15: 10, 20: 15 };
const nodePositions = [
  { x: 13.5, y: 65.2 }, { x: 19.5, y: 44.2 }, { x: 29.2, y: 22.7 }, { x: 37.6, y: 39.3 }, { x: 41.3, y: 60.8 },
  { x: 55, y: 63.1 }, { x: 59.8, y: 44.5 }, { x: 68.1, y: 29.4 }, { x: 76.6, y: 46.1 }, { x: 88, y: 59.3 }
];
const plannedSubjects = [
  { name: "专注挑战", icon: "/assets/world/tabs/tab-focus-c4d-v1.webp" },
  { name: "汉字乐园", icon: "/assets/world/tabs/tab-hanzi-c4d-v1.webp" },
  { name: "科学探索", icon: "/assets/world/tabs/tab-science-c4d-v1.webp" },
  { name: "艺术创造", icon: "/assets/world/tabs/tab-art-c4d-v1.webp" }
];

export function WorldPage() {
  const background = useLearningImage(worldBackgroundUrl, 'high');
  useEffect(preloadWorldBackground, []);
  const activeChildId = useAppStore((state) => state.activeChildId);
  const completedTaskIds = useAppStore((state) => state.completedTaskIds);
  const [subjectKey, setSubjectKey] = useState<SubjectKey>(() => (localStorage.getItem("lusky:world-subject") as SubjectKey) || "english");
  const [world, setWorld] = useState<WorldState>(() => readWorldState(activeChildId));
  const [openingChest, setOpeningChest] = useState<number | null>(null);
  const [claimingChest, setClaimingChest] = useState<number | null>(null);
  const [dragX, setDragX] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"next" | "prev" | null>(null);
  const [chapterOverride, setChapterOverride] = useState<Record<SubjectKey, number>>({ english: -1, logic: -1, math: -1 });
  const tabsRef = useRef<HTMLElement | null>(null);
  const swipeStart = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const didSwipe = useRef(false);
  const subject = WORLD_SUBJECTS[subjectKey];
  const starsFor = (id: string) => Math.min(3, Number(world.levels[id]?.stars) || (completedTaskIds.includes(id) ? 3 : 0));
  const earned = useMemo(() => subject.levels.reduce((sum, level) => sum + starsFor(level.id), 0), [subject, world, completedTaskIds]);
  const completed = subject.levels.filter((level) => starsFor(level.id) > 0).length;
  const recommendedChapter = Math.min(chapterCheckpoints.length - 1, Math.floor(completed / 10));
  const chapter = chapterOverride[subjectKey] >= 0 ? chapterOverride[subjectKey] : recommendedChapter;
  const chapterStart = chapter * 10;
  const visibleLevels = subject.levels.slice(chapterStart, chapterStart + 10);
  const milestones = [chapterStart + 5, chapterStart + 10];
  const currentAbsoluteIndex = subject.levels.findIndex((level, index) => starsFor(level.id) === 0 && (index === 0 || starsFor(subject.levels[index - 1].id) > 0));

  useEffect(() => {
    let active = true;
    setWorld(readWorldState(activeChildId));
    void loadWorldState(activeChildId).then((next) => { if (active) setWorld(next); });
    return () => { active = false; };
  }, [activeChildId]);

  useEffect(() => {
    tabsRef.current?.querySelector<HTMLButtonElement>(".active")?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [subjectKey]);

  const chooseSubject = (key: SubjectKey) => {
    setSubjectKey(key);
    localStorage.setItem("lusky:world-subject", key);
  };

  const selectChapter = (value: number) => {
    const target = Math.max(0, Math.min(chapterCheckpoints.length - 1, value));
    if (target === chapter) return;
    setSlideDirection(target > chapter ? "next" : "prev");
    setChapterOverride((current) => ({ ...current, [subjectKey]: target }));
    window.setTimeout(() => setSlideDirection(null), 380);
  };

  const finishSwipe = (clientX: number) => {
    const start = swipeStart.current;
    if (!start) return;
    const distance = clientX - start.x;
    if (Math.abs(distance) >= 52) {
      didSwipe.current = true;
      selectChapter(chapter + (distance < 0 ? 1 : -1));
      window.setTimeout(() => { didSwipe.current = false; }, 90);
    }
    swipeStart.current = null;
    setDragX(0);
  };

  const chestReady = (checkpoint: number) => {
    const start = checkpoint % 10 === 0 ? checkpoint - 10 : checkpoint - 5;
    return subject.levels.slice(start, checkpoint).every((level) => starsFor(level.id) > 0);
  };

  const claimChest = async (checkpoint: number) => {
    const id = `${subjectKey}-${checkpoint}`;
    if (claimingChest || world.chests[id]?.claimed || !chestReady(checkpoint)) return;
    setClaimingChest(checkpoint);
    const result = await claimWorldChest(activeChildId, subjectKey, checkpoint, chestRewards[checkpoint]);
    setWorld(result.state);
    setClaimingChest(null);
    if (result.newlyClaimed) {
      setOpeningChest(checkpoint);
      window.setTimeout(() => setOpeningChest(null), 2200);
    }
  };

  return <section className={`react-world page-view world-${subject.theme}`}>
    <header className="react-world-head">
      <Link className="world-back" to="/" aria-label="返回首页">←</Link>
      <div className="world-brand"><span>🪐</span><b>探索世界</b><i>✦</i></div>
      <aside className="world-wallet">
        <span><img src="/assets/rewards/lusky-unit-star-c4d-v1.webp" alt="" /><b>{earned}</b><small>能力星</small></span>
        <span><img src="/assets/rewards/lusky-diamond-c4d-v1.webp" alt="" /><b>{world.diamonds}</b><small>星河钻石</small></span>
      </aside>
    </header>

    <div className="world-tabs-row">
      <nav ref={tabsRef} className="react-subject-tabs" aria-label="选择探索科目" onWheel={(event) => {
        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) event.currentTarget.scrollLeft += event.deltaY;
      }}>
        {(Object.keys(WORLD_SUBJECTS) as SubjectKey[]).map((key) => <button key={key} className={key === subjectKey ? "active" : ""} onClick={() => chooseSubject(key)}>
          <i><img src={WORLD_SUBJECTS[key].icon} alt="" /></i><b>{WORLD_SUBJECTS[key].name}</b>
        </button>)}
        {plannedSubjects.map((item) => <button className="planned" disabled key={item.name} aria-label={`${item.name}即将开放`}><i><img src={item.icon} alt="" /></i><b>{item.name}</b><small>即将开放</small></button>)}
      </nav>
      <button className="world-tabs-more" type="button" aria-label="查看更多探索分类" onClick={() => tabsRef.current?.scrollBy({ left: 320, behavior: "smooth" })}>›</button>
    </div>

    <div className="world-map-shell">
      <section className={`react-galaxy-board ${slideDirection ? `chapter-${slideDirection}` : ""} ${dragX ? "is-dragging" : ""}`}
        style={{ "--galaxy-drag": `${dragX}px` } as React.CSSProperties}
        onPointerDown={(event) => { if (event.button !== 0 || window.matchMedia("(max-width: 760px)").matches) return; swipeStart.current = { x: event.clientX, y: event.clientY, pointerId: event.pointerId }; }}
        onPointerMove={(event) => { const start = swipeStart.current; if (!start) return; const dx = event.clientX - start.x; const dy = event.clientY - start.y; if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) { event.currentTarget.setPointerCapture(start.pointerId); setDragX(Math.max(-120, Math.min(120, dx))); } }}
        onPointerUp={(event) => finishSwipe(event.clientX)}
        onPointerCancel={() => { swipeStart.current = null; setDragX(0); }}
        onClickCapture={(event) => { if (didSwipe.current) { event.preventDefault(); event.stopPropagation(); } }}>
        <div className="world-map-canvas" style={{ backgroundImage: background.status === 'ready' ? `linear-gradient(#07175f13,#142c9526),url("${worldBackgroundUrl}")` : 'radial-gradient(ellipse at center, #463f92, #091d65)' }}>
          {background.status !== 'ready' && <div className="world-background-status" role="status">{background.status === 'error' ? <>星河背景暂未加载 <button type="button" onClick={background.retry}>重试</button></> : '正在点亮星河…'}</div>}
          <div className="world-map-status"><b>{subject.unit}</b><span>{completed}/{subject.levels.length}关 · {earned}/{subject.levels.length * 3}星</span></div>
          <div className="world-start-sign">开始</div>

          {visibleLevels.map((level, localIndex) => {
            const absoluteIndex = chapterStart + localIndex;
            const number = absoluteIndex + 1;
            const stars = starsFor(level.id);
            const unlocked = absoluteIndex === 0 || starsFor(subject.levels[absoluteIndex - 1].id) > 0;
            const current = absoluteIndex === currentAbsoluteIndex;
            const position = nodePositions[localIndex];
            const state = current ? "current" : stars ? "complete" : unlocked ? "available" : "locked";
            const className = `react-level ${state}`;
            const nodeVisual = state === "complete" ? "available" : state;
            const nodeArt = `/assets/world/nodes/world-node-${nodeVisual}-c4d-v1.webp`;
            return <div className={`react-level-wrap ${current ? "is-current" : ""}`} style={{ left: `${position.x}%`, top: `${position.y}%` }} key={level.id}>
              {unlocked ? <Link className={className} to={`/world/${subjectKey}/${number}`} aria-label={`第${number}关 ${level.title}`}>
                <img src={nodeArt} alt="" /><em>{number}</em>
              </Link> : <button className={className} disabled aria-label={`第${number}关未解锁`}><img src={nodeArt} alt="" /><em>{number}</em></button>}
              <strong>{level.title}</strong>
              <span className="react-level-stars" aria-label={`获得${stars}颗星`}>{[1, 2, 3].map((star) => <i className={star <= stars ? "filled" : ""} key={star}>★</i>)}</span>
            </div>;
          })}

          {milestones.map((checkpoint, index) => {
            const id = `${subjectKey}-${checkpoint}`;
            const claimed = Boolean(world.chests[id]?.claimed);
            const ready = chestReady(checkpoint);
            const large = checkpoint % 10 === 0;
            return <button key={checkpoint} className={`world-milestone ${large ? "large" : "small"} ${claimed ? "claimed" : ready ? "ready" : "locked"}`}
              style={{ left: index === 0 ? "48%" : "91.5%", top: index === 0 ? "82%" : "80%" }}
              onClick={() => void claimChest(checkpoint)} disabled={!ready || claimed || Boolean(claimingChest)}>
              <img src={`/assets/rewards/lusky-chest-${claimed ? "open" : "closed"}-c4d-v1.webp`} alt={claimed ? "已打开的宝箱" : "未打开的宝箱"} />
              <span><b>{checkpoint}关宝箱</b><small>{claimed ? "已领取 ✓" : ready ? `领取 ${chestRewards[checkpoint]} 钻石` : large ? "完成本航段解锁" : "完成前5关解锁"}</small></span>
            </button>;
          })}

          <button className="galaxy-nav prev" type="button" aria-label="上一航段" disabled={chapter === 0} onClick={() => selectChapter(chapter - 1)}>‹</button>
          <button className="galaxy-nav next" type="button" aria-label="下一航段" disabled={chapter === chapterCheckpoints.length - 1} onClick={() => selectChapter(chapter + 1)}>›</button>
          <div className="galaxy-next-hint">下一航段 <b>→</b></div>
        </div>
      </section>
    </div>

    <footer className="react-world-rule"><b>奖励规则</b><span>每关记录0–3颗能力星；每5关开启小宝箱，每10关开启大宝箱；所有宝箱只可领取一次。</span></footer>
    {openingChest && <div className="react-chest-celebration" role="dialog" aria-modal="true" aria-label="宝箱奖励">
      <article><span>✦ ◆ ✦</span><img src="/assets/rewards/lusky-chest-open-c4d-v1.webp" alt="打开的宝箱" /><h2>获得 {chestRewards[openingChest]} 颗钻石！</h2><p>奖励已经安全放进你的星河口袋。</p><button onClick={() => setOpeningChest(null)}>开心收下</button></article>
    </div>}
  </section>;
}
